import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

export interface CreatedByUserExtensionOptions {
	userId: string;
	saveAttribution: boolean;
}

export const CreatedByUserExtension = Extension.create<CreatedByUserExtensionOptions>({
	name: 'createdByUser',

	addOptions() {
		return {
			userId: '',
			saveAttribution: false,
		};
	},

	addGlobalAttributes() {
		// Only add attributes if attribution is enabled
		if (!this.options.saveAttribution) {
			return [];
		}

		return [
			{
				types: ['paragraph', 'heading'],
				attributes: {
					'data-created-by': {
						default: null,
						parseHTML: (el) => el.getAttribute('data-created-by'),
						renderHTML: (attrs) => (attrs['data-created-by'] ? { 'data-created-by': attrs['data-created-by'] } : {}),
					},
					'data-node-id': {
						default: null,
						parseHTML: (el) => el.getAttribute('data-node-id'),
						renderHTML: (attrs) => (attrs['data-node-id'] ? { 'data-node-id': attrs['data-node-id'] } : {}),
					},
				},
			},
		];
	},

	addStorage() {
		return {
			trackedNodes: new Set<string>(),
		};
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				appendTransaction: (transactions, oldState, newState) => {
					const { userId, saveAttribution } = this.options;
					if (!userId || !saveAttribution || !transactions.some((tx) => tx.docChanged)) {
						return null;
					}

					const tr = newState.tr;
					let modified = false;

					// Initialize trackedNodes if it doesn't exist
					if (!this.editor.storage.trackedNodes) {
						this.editor.storage.trackedNodes = new Set<string>();
					}
					const trackedNodes = this.editor.storage.trackedNodes as Set<string>;

					// Track IDs seen in this transaction to detect duplicates from splits
					const idsInThisTransaction = new Set<string>();

					// Function to generate unique node IDs
					const generateNodeId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

					newState.doc.descendants((node, pos) => {
						if (!node.isBlock || !['paragraph', 'heading'].includes(node.type.name)) {
							return true;
						}

						let nodeId = node.attrs['data-node-id'];
						const hasCreatedBy = !!node.attrs['data-created-by'];

						// If node doesn't have an ID, generate one
						if (!nodeId) {
							nodeId = generateNodeId();
							const newAttrs = {
								...node.attrs,
								'data-node-id': nodeId,
							};
							tr.setNodeMarkup(pos, node.type, newAttrs);
							modified = true;
						}

						// Check for duplicate IDs in this transaction (indicates a split)
						const isDuplicateInTransaction = idsInThisTransaction.has(nodeId);

						if (isDuplicateInTransaction) {
							// This is a duplicate ID from a split - generate new ID and attribute to current user
							const newNodeId = generateNodeId();
							const newAttrs = {
								...node.attrs,
								'data-node-id': newNodeId,
								'data-created-by': userId,
							};
							tr.setNodeMarkup(pos, node.type, newAttrs);
							trackedNodes.add(newNodeId);
							idsInThisTransaction.add(newNodeId);
							modified = true;
						} else {
							// Not a duplicate - add to transaction tracking
							idsInThisTransaction.add(nodeId);

							// Check if this is a node we've never seen before globally
							const isNewNode = !trackedNodes.has(nodeId);

							if (isNewNode) {
								// Track this node so we don't process it again
								trackedNodes.add(nodeId);

								// Only set attribution if it doesn't already have it
								if (!hasCreatedBy) {
									const newAttrs = {
										...node.attrs,
										'data-node-id': nodeId,
										'data-created-by': userId,
									};
									tr.setNodeMarkup(pos, node.type, newAttrs);
									modified = true;
								}
							}
						}

						return true;
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
